import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CheckCircle2, Clock, Download, Trash2, ExternalLink, UploadCloud, X,
  Image as ImageIcon, Film, Search, UserCircle, Sparkles, SearchX, Images,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import {
  MediaAsset,
  MediaAssetType,
  assetDisplayName,
  backfillMissingThumbnails,
  fetchOrgMediaAssets,
  galleryListStatus,
  galleryThumbSrc,
  getUserOrgId,
  isAvatarAsset,
  isImageAsset,
  isMediaApproved,
  normalizeGalleryFile,
  setMediaImagePurpose,
  uploadToGallery,
} from '../lib/mediaGallery';
import { downloadMedia, friendlyFileName } from '../lib/downloadMedia';
import EditorImagePurposeModal from '../components/editor/EditorImagePurposeModal';
import ModalOverlay from '../components/ui/ModalOverlay';
import { useImagePurposePrompt } from '../hooks/useImagePurposePrompt';
import { Link } from 'react-router-dom';
import {
  CreditProgress,
  EmptyState,
  ErrorState,
  Notice,
  Skel,
  SkeletonCards,
  SkeletonRegion,
} from '../components/ui/PageState';
import '../explore.css';
import '../gallery.css';

type TypeFilter = 'all' | MediaAssetType | 'avatar';
type StatusFilter = 'all' | 'approved' | 'pending';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export default function MediaGallery() {
  const { user } = useAuth();
  const { role } = useOrg();
  const canApprove = role === 'admin' || role === 'fin_ops';
  const fileRef = useRef<HTMLInputElement>(null);
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [query, setQuery] = useState('');
  const [preview, setPreview] = useState<MediaAsset | null>(null);
  const [retag, setRetag] = useState<MediaAsset | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const { promptPurpose, modal: purposeModal } = useImagePurposePrompt();

  const fetchAssets = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      if (!user?.id) return;
      const oid = await getUserOrgId(user.id);
      if (!oid) return;
      setOrgId(oid);
      const data = await fetchOrgMediaAssets(oid, { assetType: 'all', status: galleryListStatus(role) });
      setAssets(data);
      void backfillMissingThumbnails(oid, data, (id, thumbnailUrl) => {
        setAssets((prev) => prev.map((a) => (a.id === id ? { ...a, thumbnail_url: thumbnailUrl } : a)));
      });
    } catch (err: any) {
      setLoadError(err.message || 'Falha ao carregar a galeria.');
    } finally {
      setLoading(false);
    }
  }, [user?.id, role]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const handleApprove = async (id: string) => {
    try {
      const { supabase } = await import('../lib/supabase');
      const { error: updateError } = await supabase
        .from('media_assets')
        .update({ status: 'approved' })
        .eq('id', id);
      if (updateError) throw updateError;
      setAssets((prev) => prev.map((a) => (a.id === id ? { ...a, status: 'approved' } : a)));
      setNotice('Mídia liberada para uso.');
    } catch (err: any) {
      setError('Não foi possível liberar a mídia: ' + err.message);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { supabase } = await import('../lib/supabase');
      const { error: deleteError } = await supabase.from('media_assets').delete().eq('id', id);
      if (deleteError) throw deleteError;
      setAssets((prev) => prev.filter((a) => a.id !== id));
      if (preview?.id === id) setPreview(null);
      setConfirmDeleteId(null);
      setNotice('Item removido da galeria.');
    } catch (err: any) {
      setError('Não foi possível remover o item: ' + err.message);
    }
  };

  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    if (!notice) return;
    const t = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(t);
  }, [notice]);

  const ingestLocalFiles = async (picked: File[]) => {
    if (!picked.length) return;
    if (!orgId) {
      setError('Organização não encontrada. Recarregue a página e tente de novo.');
      return;
    }
    const images: File[] = [];
    const others: File[] = [];
    for (const raw of picked) {
      const file = normalizeGalleryFile(raw);
      if (file.type.startsWith('image/') || /\.(jpe?g|png|webp|gif|heic|heif|avif)$/i.test(file.name)) {
        images.push(file);
      } else {
        others.push(file);
      }
    }
    setError(null);
    const uploaded: MediaAsset[] = [];
    try {
      if (others.length) {
        setUploading(true);
        for (const file of others) {
          uploaded.push(await uploadToGallery(orgId, file));
        }
      }
      for (let i = 0; i < images.length; i++) {
        const file = images[i];
        const previewUrl = URL.createObjectURL(file);
        const purpose = await promptPurpose({
          preview: previewUrl,
          label: file.name.replace(/\.[^.]+$/, ''),
          remaining: images.length - i,
        });
        URL.revokeObjectURL(previewUrl);
        if (!purpose) {
          setError('Escolha se a imagem é Avatar (rosto) ou Foto (cena) para concluir o envio.');
          continue;
        }
        setUploading(true);
        uploaded.push(await uploadToGallery(orgId, file, { purpose }));
      }
      if (uploaded.length) {
        setAssets((prev) => [...uploaded, ...prev]);
        setStatusFilter('all');
        setTypeFilter('all');
        setNotice(uploaded.length === 1 ? '1 arquivo enviado para a galeria.' : `${uploaded.length} arquivos enviados para a galeria.`);
      }
    } catch (err: any) {
      setError(err.message || 'Falha no upload.');
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) {
      setError('Nenhum arquivo válido foi selecionado.');
      return;
    }
    await ingestLocalFiles(files);
  };

  const filtered = assets.filter((a) => {
    const type = (a.asset_type || 'video') as MediaAssetType;
    if (typeFilter === 'avatar') {
      if (!isAvatarAsset(a)) return false;
    } else if (typeFilter !== 'all' && type !== typeFilter) {
      return false;
    }
    const pending = !isMediaApproved(a);
    if (statusFilter === 'approved' && pending) return false;
    if (statusFilter === 'pending' && !pending) return false;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      if (!assetDisplayName(a).toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const counts = {
    all: assets.length,
    image: assets.filter((a) => isImageAsset(a)).length,
    video: assets.filter((a) => !isImageAsset(a)).length,
    avatar: assets.filter((a) => isAvatarAsset(a)).length,
    pending: assets.filter((a) => !isMediaApproved(a)).length,
  };

  const hasActiveFilters = typeFilter !== 'all' || statusFilter !== 'all' || query.trim() !== '';
  const clearFilters = () => {
    setTypeFilter('all');
    setStatusFilter('all');
    setQuery('');
  };

  if (loading) {
    return (
      <div className="gallery-page">
        <SkeletonRegion label="Carregando a galeria…">
          <div className="gallery-head">
            <div className="sk-stack" style={{ flex: 1, minWidth: 240 }}>
              <Skel w={160} h={30} />
              <Skel w="70%" h={14} />
            </div>
            <Skel className="" w={130} h={44} style={{ borderRadius: 12 }} />
          </div>
          <div className="gallery-toolbar">
            <Skel className="sk-chip" w={90} />
            <Skel className="sk-chip" w={110} />
            <Skel className="sk-chip" w={110} />
            <Skel className="sk-chip" w={110} />
          </div>
          <SkeletonCards count={8} ratio="5 / 4" />
        </SkeletonRegion>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="gallery-page">
        <header className="gallery-head">
          <div>
            <h1>Galeria</h1>
            <p className="subtitle">Fotos e vídeos da organização.</p>
          </div>
        </header>
        <ErrorState
          title="Não foi possível carregar a galeria"
          message="Suas mídias continuam salvas. Tente carregar a lista de novo."
          detail={loadError}
          onRetry={() => void fetchAssets()}
        />
      </div>
    );
  }

  const filterChip = (active: boolean, onClick: () => void, children: React.ReactNode) => (
    <button type="button" className={`explore-chip${active ? ' is-on' : ''}`} aria-pressed={active} onClick={onClick}>
      {children}
    </button>
  );

  const uploadDisabled = uploading || !orgId;

  return (
    <div
      className="gallery-page"
      onDragEnter={(e) => {
        if (![...e.dataTransfer.types].includes('Files')) return;
        dragDepth.current += 1;
        setDragging(true);
      }}
      onDragLeave={(e) => {
        if (![...e.dataTransfer.types].includes('Files')) return;
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragging(false);
      }}
      onDragOver={(e) => {
        if (![...e.dataTransfer.types].includes('Files')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      }}
      onDrop={(e) => {
        if (![...e.dataTransfer.types].includes('Files')) return;
        e.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        void ingestLocalFiles(Array.from(e.dataTransfer.files || []));
      }}
    >
      {dragging && (
        <div className="gallery-drop" aria-hidden>
          <span><UploadCloud size={22} /> Solte aqui para enviar à galeria</span>
        </div>
      )}

      <header className="gallery-head">
        <div>
          <h1>Galeria</h1>
          <p className="subtitle">
            Fotos e vídeos da organização. Uploads manuais já ficam disponíveis; gerações de IA entram como pendentes até serem liberadas. Você também pode arrastar arquivos para esta página.
          </p>
        </div>
        <label className={`explore-btn is-primary is-lg gallery-upload${uploadDisabled ? ' is-disabled' : ''}`}>
          <UploadCloud size={18} aria-hidden />
          {uploading ? 'Enviando…' : 'Enviar arquivos'}
          <input
            ref={fileRef}
            type="file"
            multiple
            disabled={uploadDisabled}
            onChange={handleUpload}
            aria-label="Enviar arquivos para a galeria"
          />
        </label>
      </header>

      <div className="gallery-stack">
        {uploading && (
          <CreditProgress
            label="Enviando para a galeria…"
            detail="Mantenha esta página aberta até terminar. Enviar arquivos não gasta créditos."
          />
        )}
        {error && (
          <Notice tone="error" actions={<button type="button" className="explore-btn is-sm" onClick={() => setError(null)}>Dispensar aviso</button>}>
            {error}
          </Notice>
        )}
        {notice && <Notice tone="success">{notice}</Notice>}
      </div>

      {assets.length > 0 && (
        <div className="gallery-toolbar">
          <div className="explore-chips" role="group" aria-label="Filtrar por tipo">
            {filterChip(typeFilter === 'all', () => setTypeFilter('all'), <>Todos ({counts.all})</>)}
            {filterChip(typeFilter === 'image', () => setTypeFilter('image'), <><ImageIcon size={14} aria-hidden /> Fotos ({counts.image})</>)}
            {filterChip(typeFilter === 'video', () => setTypeFilter('video'), <><Film size={14} aria-hidden /> Vídeos ({counts.video})</>)}
            {filterChip(typeFilter === 'avatar', () => setTypeFilter('avatar'), <><UserCircle size={14} aria-hidden /> Avatares ({counts.avatar})</>)}
            {canApprove &&
              filterChip(
                statusFilter === 'pending',
                () => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending'),
                <>Pendentes ({counts.pending})</>,
              )}
          </div>
          <div className="explore-search">
            <Search size={16} aria-hidden />
            <input
              type="search"
              aria-label="Buscar na galeria pelo nome"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar pelo nome"
            />
          </div>
          <p className="gallery-count" role="status" aria-live="polite">
            {filtered.length === 1 ? '1 item' : `${filtered.length} itens`}
            {hasActiveFilters ? ' com os filtros atuais' : ''}
          </p>
        </div>
      )}

      {assets.length === 0 ? (
        <EmptyState
          icon={<Images size={24} />}
          title="Sua galeria está vazia"
          text="Envie fotos, vídeos ou áudios do seu computador, ou arraste os arquivos para esta página. O que você gerar com IA também aparece aqui."
          actions={
            <Link to="/painel-criacao" className="explore-btn">
              <Sparkles size={16} aria-hidden /> Gerar no Painel de Criação
            </Link>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<SearchX size={24} />}
          title="Nenhuma mídia com esses filtros"
          text="Tente outro nome ou volte para ver todos os itens da galeria."
          actions={<button type="button" className="explore-btn" onClick={clearFilters}>Limpar filtros e busca</button>}
        />
      ) : (
        <div className="gallery-grid">
          {filtered.map((asset) => {
            const image = isImageAsset(asset);
            const approved = isMediaApproved(asset);
            const name = assetDisplayName(asset);
            const avatar = isAvatarAsset(asset);
            const kindLabel = avatar ? 'Avatar' : image ? 'Foto' : 'Vídeo';
            const confirming = confirmDeleteId === asset.id;
            return (
              <article key={asset.id} className="gallery-card" aria-label={name}>
                <div className="gallery-media">
                  <button type="button" className="gallery-media-open" onClick={() => setPreview(asset)} aria-label={`Ver ${kindLabel.toLowerCase()}: ${name}`}>
                    {image ? (
                      <img src={galleryThumbSrc(asset)} alt="" loading="lazy" decoding="async" />
                    ) : (
                      <video
                        src={asset.video_url}
                        onMouseEnter={(e) => {
                          if (prefersReducedMotion()) return;
                          const vid = e.currentTarget;
                          const p = vid.play();
                          if (p !== undefined) p.catch(() => { /* interrupted by pause — ignore */ });
                        }}
                        onMouseLeave={(e) => {
                          const vid = e.currentTarget;
                          vid.pause();
                          try { vid.currentTime = 0; } catch { /* ignore seek race */ }
                        }}
                        muted
                        loop
                        playsInline
                        preload="metadata"
                      />
                    )}
                  </button>
                  {image ? (
                    <button
                      type="button"
                      className="gallery-pill is-left"
                      onClick={() => setRetag(asset)}
                      aria-label={`${kindLabel}. Alterar para Avatar ou Foto`}
                      title="Definir se é Avatar ou Foto"
                    >
                      {avatar ? <UserCircle size={12} aria-hidden /> : <ImageIcon size={12} aria-hidden />}
                      {kindLabel}
                    </button>
                  ) : (
                    <span className="gallery-pill is-left">
                      <Film size={12} aria-hidden /> {kindLabel}
                    </span>
                  )}
                  <span className={`gallery-pill is-right ${approved ? 'is-ok' : 'is-wait'}`}>
                    {approved ? <CheckCircle2 size={12} aria-hidden /> : <Clock size={12} aria-hidden />}
                    {approved ? 'Aprovado' : 'Pendente'}
                  </span>
                </div>

                <div className="gallery-body">
                  <div>
                    <h2 className="gallery-name" title={name}>{name}</h2>
                    <p className="gallery-meta">
                      {asset.created_at ? new Date(asset.created_at).toLocaleString('pt-BR') : '—'}
                      {asset.generation_id ? ' · Gerado com IA' : ' · Upload'}
                    </p>
                  </div>

                  {confirming ? (
                    <div className="gallery-confirm" role="alert">
                      <p><strong>Remover da galeria?</strong> Essa ação não pode ser desfeita.</p>
                      <div className="gallery-confirm-actions">
                        <button type="button" className="explore-btn" autoFocus onClick={() => setConfirmDeleteId(null)}>Manter</button>
                        <button type="button" className="explore-btn is-danger" onClick={() => void handleDelete(asset.id)}>Remover</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {!approved && canApprove && (
                        <button type="button" className="explore-btn is-ok" onClick={() => void handleApprove(asset.id)}>
                          <CheckCircle2 size={16} aria-hidden /> Liberar para uso
                        </button>
                      )}
                      <div className="gallery-actions">
                        <a href={asset.video_url} target="_blank" rel="noreferrer" className="explore-btn is-grow">
                          <ExternalLink size={16} aria-hidden /> Abrir
                        </a>
                        <button
                          type="button"
                          className="gallery-icon-btn"
                          aria-label={`Baixar ${name}`}
                          title="Baixar"
                          onClick={() => void downloadMedia(asset.video_url, friendlyFileName({
                            reference: name,
                            url: asset.video_url,
                            at: asset.created_at ? new Date(asset.created_at) : undefined,
                            fallbackExt: image ? 'png' : 'mp4',
                          }))}
                        >
                          <Download size={16} aria-hidden />
                        </button>
                        <button
                          type="button"
                          className="gallery-icon-btn is-danger"
                          aria-label={`Remover ${name}`}
                          title="Remover"
                          onClick={() => setConfirmDeleteId(asset.id)}
                        >
                          <Trash2 size={16} aria-hidden />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {preview && (
        <ModalOverlay className="modal-overlay gallery-viewer" label={`Pré-visualização: ${assetDisplayName(preview)}`} onClose={() => setPreview(null)}>
          <div className="gallery-viewer-body" onClick={(e) => e.stopPropagation()}>
            <div className="gallery-viewer-bar">
              <h2 className="gallery-viewer-title">{assetDisplayName(preview)}</h2>
              <button type="button" className="gallery-icon-btn" onClick={() => setPreview(null)} aria-label="Fechar pré-visualização">
                <X size={18} aria-hidden />
              </button>
            </div>
            {isImageAsset(preview) ? (
              <img src={preview.video_url} alt={assetDisplayName(preview)} />
            ) : (
              <video src={preview.video_url} controls controlsList="nodownload" autoPlay playsInline />
            )}
          </div>
        </ModalOverlay>
      )}

      {purposeModal}
      <EditorImagePurposeModal
        open={!!retag}
        preview={retag ? galleryThumbSrc(retag) : undefined}
        label={retag ? assetDisplayName(retag) : undefined}
        onPick={async (purpose) => {
          if (!retag) return;
          try {
            const generation_context = await setMediaImagePurpose(retag.id, purpose, retag.generation_context);
            setAssets((prev) => prev.map((a) => (a.id === retag.id ? { ...a, generation_context } : a)));
          } catch (err: any) {
            setError(err.message || 'Falha ao marcar a imagem.');
          } finally {
            setRetag(null);
          }
        }}
        onClose={() => setRetag(null)}
      />
    </div>
  );
}
